import { useEffect, useRef, useState } from 'react';
import { ResponseError, type Invitation, type RoleEnum, type Team, type TeamsApi } from 'api-client';
import InvitationManagement from './InvitationManagement';

interface TeamInvitationsSettingsProps {
  team: Team;
  apiClient: TeamsApi;
  onTeamUpdate: (team: Team) => void;
}
export default function TeamInvitationsSettings({ team, apiClient, onTeamUpdate }: TeamInvitationsSettingsProps) {
  const generation = useRef<number>(0);
  useEffect(() => {
    generation.current += 1;
    setPending(false);
    setError(null);
    setSuccess(null);
    return () => {
      generation.current += 1;
    };
  }, [team.id]);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const action = async (run: () => Promise<{ team?: Team; message: string }>): Promise<boolean> => {
    if (pending || !team.isAdmin) return false;
    const current = generation.current;
    setPending(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await run();
      if (current !== generation.current) return false;
      if (result.team) onTeamUpdate(result.team);
      setSuccess(result.message);
      return true;
    } catch (failure) {
      let message = 'Could not complete the invitation action. Please retry.';
      if (failure instanceof ResponseError) {
        const body: unknown = await failure.response.json().catch((): null => null);
        if (body && typeof body === 'object') {
          const detail: unknown = Reflect.get(body, 'detail');
          const email: unknown = Reflect.get(body, 'email');
          if (typeof detail === 'string') message = detail;
          else if (Array.isArray(email) && email.every((value) => typeof value === 'string')) message = email.join(' ');
        }
      }
      if (current === generation.current) setError(message);
      return false;
    } finally {
      if (current === generation.current) setPending(false);
    }
  };
  const send = (email: string, role: RoleEnum) =>
    action(async () => {
      const invitation = await apiClient.invitationsCreate({
        teamSlug: team.slug || '',
        invitation: { team: team.id, email, role },
      });
      return {
        team: { ...team, invitations: [...(team.invitations || []), invitation] },
        message: `Invitation sent to ${invitation.email}.`,
      };
    });
  const resend = (invitation: Invitation) =>
    action(async () => {
      const result = await apiClient.invitationsResend({ teamSlug: team.slug || '', id: invitation.id });
      return { message: `Invitation resent to ${result.email}.` };
    });
  const cancel = (invitation: Invitation) =>
    action(async () => {
      await apiClient.invitationsDestroy({ teamSlug: team.slug || '', id: invitation.id });
      return {
        team: { ...team, invitations: (team.invitations || []).filter((value) => value.id !== invitation.id) },
        message: `Invitation to ${invitation.email} cancelled.`,
      };
    });
  return (
    <InvitationManagement
      team={team}
      pending={pending}
      error={error}
      success={success}
      onSend={send}
      onResend={(value) => {
        void resend(value);
      }}
      onCancel={cancel}
    />
  );
}
